/**
 * frontend/src/pages/valuation/_FinancialsUploader.tsx
 *
 * VAL-UPLOAD-U1B-1 — the drop zone. Mirrors the QoE uploader's shape (react-dropzone,
 * 25 MB) without importing it. A refused file is refused here, on screen, before
 * any upload call; the words match the server's so which side caught it is not visible.
 */
import { useDropzone } from 'react-dropzone'
import { Loader2, Upload } from 'lucide-react'

const MAX_BYTES = 25 * 1024 * 1024 // matches the server's limit
export const ACCEPT_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.csv']

export const UNSUPPORTED_TYPE = "That file type isn't supported. Upload a PDF, Excel, or CSV."
const tooLarge = (bytes: number) =>
  `This file is ${(bytes / (1024 * 1024)).toFixed(1)} MB — the limit is 25 MB. Upload a smaller export or split it.`

/** The reason a file must not be uploaded, or null when it may be. */
export function refusalFor(file: File): string | null {
  const name = file.name.toLowerCase()
  if (!ACCEPT_EXTENSIONS.some(ext => name.endsWith(ext))) return UNSUPPORTED_TYPE
  if (file.size > MAX_BYTES) return tooLarge(file.size)
  return null
}

interface Props {
  busy: boolean
  /** Files that passed the checks. */
  onFiles: (files: File[]) => void
  /** A refusal message to show on screen. */
  onRefused: (message: string) => void
}

export function FinancialsUploader({ busy, onFiles, onRefused }: Props) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: busy,
    onDrop: (files: File[]) => {
      const ok: File[] = []
      for (const f of files) {
        const why = refusalFor(f)
        if (why) onRefused(why)
        else ok.push(f)
      }
      if (ok.length) onFiles(ok)
    },
  })

  return (
    <div
      {...getRootProps()}
      className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-8 text-center ${
        isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'
      }`}
    >
      <input {...getInputProps({ accept: ACCEPT_EXTENSIONS.join(',') })} />
      {busy ? <Loader2 className="h-6 w-6 animate-spin text-gray-500" /> : <Upload className="h-6 w-6 text-gray-500" />}
      <p className="text-sm font-medium text-gray-900">Drop a file here, or click to choose one</p>
      <p className="text-xs text-gray-500">PDF, Excel or CSV · up to 25 MB</p>
    </div>
  )
}
